<template>
  <UPage>
    <ClientOnly>
      <UPageBody>
        <UContainer>
          <div v-if="ready && !savedModels.length" class="text-sm py-32">
            No saved models yet. Go to the
            <NuxtLink to="/products" class="underline">Line Overview</NuxtLink> to add some models to the cart.
          </div>
          <div v-else-if="ready">
            <UPageGrid>
              <WorkbookLineOverview class="col-span-12" :categories="savedCategories" :show-quantities="true" />
            </UPageGrid>
          </div>
        </UContainer>
      </UPageBody>
      <div class="fixed bottom-0 pb-4 w-full border-t-2 bg-white pt-4" v-motion-slide-bottom :delay="1000">
        <UContainer>
          <UPageGrid>
            <section class="col-span-12">
              <div v-if="ready" v-motion-pop class="flex items-start md:items-center gap-12 justify-between">
                <h1 class="hidden md:inline text-xl font-semibold mb-1">Order summary:</h1>
                <div class="hidden md:block">
                  {{ modelsWithQtyCount }} model<span v-if="modelsWithQtyCount !== 1">s</span>
                </div>
                <div class="hidden md:block">
                  {{ totalSkusWithQty }} SKU<span v-if="totalSkusWithQty !== 1">s</span>
                </div>
                <div class="hidden md:block">
                  {{ totalPcs }} total item<span v-if="totalPcs !== 1">s</span><br />
                </div>
                <div>
                  Total value: <strong>{{ formatNumber(orderTotalValue, currency) }}</strong>
                </div>
                <UDrawer direction="bottom" v-model:open="detailsDrawerOpen">
                  <UButton class="p-0" label="Details & more" color="neutral" variant="link"
                    trailing-icon="i-lucide-chevrons-up" />
                  <template #body>
                    <UContainer>
                      <UPageGrid class="border-b-2">
                        <section
                          class="col-span-7 flex flex-col md:flex-row items-start md:items-center justify-between pb-4">
                          <h1 class="text-xl font-semibold mb-1">Order summary:</h1>
                          <div>
                            {{ modelsWithQtyCount }} model<span v-if="modelsWithQtyCount !== 1">s</span>
                          </div>
                          <div>
                            {{ totalSkusWithQty }} SKU<span v-if="totalSkusWithQty !== 1">s</span>
                          </div>
                          <div>
                            {{ totalPcs }} total item<span v-if="totalPcs !== 1">s</span><br />
                          </div>
                          <div>
                            Total value: <strong>{{ formatNumber(orderTotalValue, currency) }}</strong>
                          </div>
                        </section>
                        <section class="col-span-5">
                          <div class="flex flex-col md:flex-row justify-center md:justify-end gap-4 w-full mb-4 ">
                            <!-- Save Cart Button -->
                            <UPopover v-if="isDev" v-model:open="saveCartPopoverOpen">
                              <UButton size="md" color="info" class="flex justify-center">
                                Save Cart
                              </UButton>
                              <template #content>
                                <div class="p-4 w-80">
                                  <h3 class="w-full text-center mb-2 font-bold">Save cart</h3>
                                  <p class="text-sm mb-3">
                                    Enter a name for this cart.
                                  </p>
                                  <UInput v-model="saveCartName" type="text" placeholder="Cart Name" size="md" class="mb-2 w-full" />
                                  <UButton size="md" color="info" block :loading="isSavingCart" @click="saveCartToDatabase">Save</UButton>
                                  <div v-if="saveCartError" class="mt-2 text-red-600 text-xs">{{ saveCartError }}</div>
                                  <div v-if="saveCartSuccess" class="mt-2 text-green-600 text-xs">{{ saveCartSuccess }}</div>
                                </div>
                              </template>
                            </UPopover>
                            <!-- Share Cart Button -->
                            <UPopover v-model:open="shareCartPopoverOpen">
                              <UButton size="md" color="primary" class="flex justify-center">
                                Share this cart
                              </UButton>
                              <template #content>
                                <div class="p-4 w-80">
                                  <h3 class="w-full text-center mb-2 font-bold">Share cart</h3>
                                  <p class="text-sm mb-3">
                                    Enter a name for this cart and the recipient's e-mail address.
                                    This cart will also be saved to your saved carts.
                                  </p>
                                  <UInput v-model="shareCartName" type="text" placeholder="Cart Name (optional)" size="md" class="mb-2 w-full" />
                                  <UInput v-model="shareEmail" type="email" placeholder="Recipient e-mail" size="md" class="mb-2 w-full" />
                                  <UButton size="md" color="primary" block :loading="isSharingCart" @click="shareCart">Send</UButton>
                                  <div v-if="shareError" class="mt-2 text-red-600 text-xs">{{ shareError }}</div>
                                  <div v-if="shareSuccess" class="mt-2 text-green-600 text-xs">{{ shareSuccess }}</div>
                                </div>
                              </template>
                            </UPopover>
                            <UPopover v-model:open="detailsDrawerClearCartPopoverOpen">
                              <UButton size="md" color="warning" class="flex justify-center">
                                {{ cart.currentOrderEdit ? 'Cancel order edit' : 'Clear cart' }}
                              </UButton>
                              <template #content>
                                <div class="p-4">
                                  <h3 class="w-full text-center mb-2">Are you sure?</h3>
                                  <p class="text-sm mb-2">
                                    {{ cart.currentOrderEdit ? 'This will cancel the order edit and clear the cart' : 'This will remove ALL quantities and products' }}
                                  </p>
                                  <div class="flex gap-2">
                                    <UButton :block="true" size="sm" color="primary" @click="clearCartConfirm">Yes
                                    </UButton>
                                    <UButton :block="true" size="sm" variant="outline" @click="closeClearCartPopover">No
                                    </UButton>
                                  </div>
                                </div>
                              </template>
                            </UPopover>
                            <UPopover v-if="cart.currentOrderEdit" v-model:open="saveOrderPopoverOpen">
                              <UButton size="md" color="primary" :disabled="totalPcs === 0 || isPlacingOrder"
                                :loading="isPlacingOrder">
                                Update  {{ cart.currentOrderEdit.id }}
                              </UButton>
                              <template #content>
                                <div class="p-4 w-80">
                                  <h3 class="w-full text-center mb-2">Are you sure?</h3>
                                  <p class="text-sm mb-3">This will update order <strong>{{ cart.currentOrderEdit.id }}</strong> and a new confirmation will be sent to <strong>{{ cart.currentOrderEdit.userEmail }}</strong>.</p>
                                  <div class="flex gap-2">
                                    <UButton :block="true" size="sm" color="primary" @click="confirmSaveOrder">
                                      Yes
                                    </UButton>
                                    <UButton :block="true" size="sm" variant="outline" @click="closeSaveOrderPopover">
                                      No
                                    </UButton>
                                  </div>
                                </div>
                              </template>
                            </UPopover>
                            <UButton v-else size="md" color="primary" :disabled="totalPcs === 0 || isPlacingOrder"
                              :loading="isPlacingOrder" @click="goToConfirm" class="flex justify-center">
                              Review order
                            </UButton>
                          </div>
                        </section>
                      </UPageGrid>
                      <div class="pb-32">
                        <div v-if="categorySummaries.length"
                          class="flex flex-col md:flex-row gap-4 pb-4 border-b border-neutral-200">
                          <div v-for="cat in categorySummaries" :key="cat.slug"
                            class="p-4 last:border-none last:pb-0 cursor-pointer transition-colors hover:bg-workbook-light-grey/10"
                            @click="scrollToCategory(cat.slug)" tabindex="0" role="button">
                            <p class="m-0 text-[12px] leading-snug">
                              <span class="font-semibold">{{ cat.name }}</span><br />
                              – {{ cat.modelsWithQty }} model<span v-if="cat.modelsWithQty !== 1">s</span><br />
                              – {{ cat.skusWithQty }} SKU<span v-if="cat.skusWithQty !== 1">s</span><br />
                              – {{ cat.totalItems }} item<span v-if="cat.totalItems !== 1">s</span><br />
                              Subtotal: {{ formatNumber(cat.value, currency) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </UContainer>
                  </template>
                </UDrawer>
                <div class="flex gap-4 items-center">
                  <UPopover v-model:open="clearCartPopoverOpen" v-if="!isMobile">
                    <UButton size="md" color="warning">
                      {{ cart.currentOrderEdit ? 'Cancel order edit' : 'Clear cart' }}
                    </UButton>
                    <template #content>
                      <div class="p-4">
                        <h3 class="w-full text-center mb-2">Are you sure?</h3>
                        <p class="text-sm mb-2">
                          {{ cart.currentOrderEdit ? 'This will cancel the order edit and clear the cart' : 'This will remove ALL quantities and products' }}
                        </p>
                        <div class="flex gap-2">
                          <UButton :block="true" size="sm" color="primary" @click="clearCartConfirm">Yes
                          </UButton>
                          <UButton :block="true" size="sm" variant="outline" @click="closeClearCartPopover">No
                          </UButton>
                        </div>
                      </div>
                    </template>
                  </UPopover>
                  <UPopover v-if="cart.currentOrderEdit" v-model:open="saveOrderPopoverOpen">
                    <UButton size="md" color="primary" :disabled="totalPcs === 0 || isPlacingOrder"
                      :loading="isPlacingOrder">
                      Update  {{ cart.currentOrderEdit.id }}
                    </UButton>
                    <template #content>
                      <div class="p-4 w-80">
                        <h3 class="w-full text-center mb-2">Are you sure?</h3>
                        <p class="text-sm mb-3">This will update order <strong>{{ cart.currentOrderEdit.id }}</strong> and a new confirmation will be sent to <strong>{{ cart.currentOrderEdit.userEmail }}</strong>.</p>
                        <div class="flex gap-2">
                          <UButton :block="true" size="sm" color="primary" @click="confirmSaveOrder">
                            Yes
                          </UButton>
                          <UButton :block="true" size="sm" variant="outline" @click="closeSaveOrderPopover">
                            No
                          </UButton>
                        </div>
                      </div>
                    </template>
                  </UPopover>
                  <UButton v-else size="md" color="primary" :disabled="totalPcs === 0 || isPlacingOrder"
                    :loading="isPlacingOrder" @click="goToConfirm">
                    Review order
                  </UButton>
                </div>
              </div>
            </section>
          </UPageGrid>
        </UContainer>
      </div>
    </ClientOnly>
    <!-- Modal for overwrite cart -->
    <UModal :fullscreen="false" 
      v-model:open="overwriteCartModalOpen"
      title="Load shared cart?"
    >
      <template #content>
        <div class="flex flex-col items-center mx-auto p-8">
          <p class="text-base mb-2 text-center">Choose how to handle this shared cart:</p>
          <div v-if="overwriteCartData" class="mb-6 text-center text-sm space-y-1">
            <div><strong>Cart name:</strong> {{ overwriteCartData.name || '—' }}</div>
            <div><strong>Created by:</strong> {{ overwriteCartData.creatorEmail || '—' }}</div>
            <div><strong>Number of products:</strong> {{ cartProductCount(overwriteCartData) }}</div>
          </div>
          <div class="flex flex-col gap-3 w-full max-w-md">
            <UButton 
              v-if="auth.isAuthenticated"
              color="info" 
              label="Save to my carts" 
              @click="saveSharedCartToMyCarts" 
              :loading="isSavingSharedCart"
              block
            />
            <UButton 
              color="primary" 
              label="Load as current cart" 
              @click="showOverwriteConfirmation" 
              block
            />
            <UButton 
              color="neutral" 
              label="Cancel" 
              @click="overwriteCartModalOpen = false" 
              variant="outline"
              block
            />
          </div>
        </div>
      </template>
    </UModal>
    <!-- Modal for overwrite confirmation -->
    <UModal :fullscreen="false" 
      v-model:open="overwriteConfirmModalOpen"
      title="Confirm overwrite?"
    >
      <template #content>
        <div class="flex flex-col items-center mx-auto p-8">
          <p class="text-base mb-4 text-center">Overwrite current cart</p>
          <div class="flex gap-2">            
            <UButton color="primary" label="Yes" @click="acceptOverwriteSharedCart" />
            <UButton color="neutral" label="No" @click="overwriteConfirmModalOpen = false" variant="outline" />
          </div>
        </div>
      </template>
    </UModal>
  </UPage>
</template>

<script setup lang="ts">
import { computed, onMounted, watchEffect, ref } from 'vue'
import { useCartStore } from '@/store/cart'
import { useCatalogStore } from '@/store/catalog'
import { useSettingsStore } from '@/store/settings'
import { useOrdersStore } from '@/store/orders'
import { useAuthStore } from '@/store/auth'
import { useRoute, useRouter } from 'vue-router'
import type { ProductModel, CategoryModel } from '@/types/catalog'
import WorkbookLineOverview from '@/components/workbook/LineOverview.vue'
import { formatNumber } from '@/utils/formatNumber'
import { useWindowSize } from '@vueuse/core'

const isDev = import.meta.dev

const { width } = useWindowSize()

const toast = useToast()

const cart = useCartStore()
const catalog = useCatalogStore()
const settings = useSettingsStore()
const ordersStore = useOrdersStore()
const auth = useAuthStore()

const router = useRouter()
const route = useRoute()

const isPlacingOrder = ref(false)
const isSavingCart = ref(false)
const isSharingCart = ref(false)
const shareEmail = ref('')
const shareCartName = ref('')
const shareError = ref('')
const shareSuccess = ref('')
const shareCartPopoverOpen = ref(false)
const saveCartPopoverOpen = ref(false)
const saveCartName = ref('')
const saveCartError = ref('')
const saveCartSuccess = ref('')

const overwriteCartModalOpen = ref(false)
const overwriteCartCreatorEmail = ref('')
const overwriteCartData = ref(null)
const isSavingSharedCart = ref(false)
const overwriteConfirmModalOpen = ref(false)

const isMobile = computed(() => width.value < 767)

onMounted(() => {
  cart.hydrate()
  settings.hydrate()
  ordersStore.hydrate()
  loadSharedCartIfPresent()
})

function goToConfirm() {
  router.push('/confirm')
}

const currency = computed(() => settings.currency)
const ready = computed(() => cart.hydrated && catalog.items.length > 0)

const clearCartPopoverOpen = ref(false)
const detailsDrawerClearCartPopoverOpen = ref(false)
const saveOrderPopoverOpen = ref(false)
const detailsDrawerOpen = ref(false)

function clearCartConfirm() {
  cart.clearAllSavedStyles()
  for (const sku in cart.lineItems) {
    cart.lineItems[sku].qty = 0
  }
  cart.clearOrderEdit()
  cart.persist()
  clearCartPopoverOpen.value = false
  detailsDrawerClearCartPopoverOpen.value = false // Stäng båda (ifall någon är öppen)
  detailsDrawerOpen.value = false
}


function wsFieldForCurrency(cur: string): string {
  switch (cur) {
    case 'USD': return 'WSUSD'
    case 'EUR': return 'WSEUR'
    case 'GBP': return 'WSGBP'
    case 'CHF': return 'WSCHF'
    case 'CAD':
    default:
      return 'WSCAD'
  }
}

// --- SAVE CART TO DATABASE ---
async function saveCartToDatabase() {
  saveCartError.value = ''
  saveCartSuccess.value = ''
  isSavingCart.value = true
  try {
    const { $firestore } = useNuxtApp()
    if (!$firestore) throw new Error('Firestore not available')
    const { doc, setDoc, collection, Timestamp } = await import('firebase/firestore')
    const cartId = 'cart-' + Date.now() + '-' + Math.floor(Math.random() * 100000)
    const sharedCartDoc = doc(collection($firestore, 'sharedCarts'), cartId)
    const payload = {
      id: cartId,
      name: saveCartName.value.trim() || '',
      items: Object.fromEntries(
        Object.entries(cart.lineItems)
          .filter(([, li]) => li.qty > 0)
          .map(([sku, li]) => [sku, { qty: li.qty }])
      ),
      creatorEmail: auth.user?.email || '',
      timestamp: Timestamp.fromMillis(Date.now())
    }
    await setDoc(sharedCartDoc, payload)
    saveCartSuccess.value = 'Cart saved!'
    toast.add({ title: 'Cart saved', description: 'Your cart was saved', color: 'success' })
    saveCartName.value = ''
    setTimeout(() => {
      saveCartPopoverOpen.value = false
    }, 1500)
  } catch (err) {
    saveCartError.value = 'Failed to save cart: ' + (err?.message || '')
    toast.add({ title: 'Save failed', description: 'Could not save cart to database', color: 'error' })
  } finally {
    isSavingCart.value = false
  }
}

// --- SHARE CART FUNCTIONALITY ---
async function shareCart() {
  shareError.value = ''
  shareSuccess.value = ''
  isSharingCart.value = true
  try {
    // Save new cart to sharedCarts in Firestore
    const { $firestore } = useNuxtApp()
    if (!$firestore) throw new Error('Firestore not available')
    const { doc, setDoc, collection, Timestamp } = await import('firebase/firestore')
    const cartId = 'cart-' + Date.now() + '-' + Math.floor(Math.random() * 100000)
    const sharedCartDoc = doc(collection($firestore, 'sharedCarts'), cartId)
    const payload = {
      id: cartId,
      name: shareCartName.value.trim() || '',
      items: Object.fromEntries(
        Object.entries(cart.lineItems)
          .filter(([, li]) => li.qty > 0)
          .map(([sku, li]) => [sku, { qty: li.qty }])
      ),
      creatorEmail: auth.user?.email || '',
      timestamp: Timestamp.fromMillis(Date.now())
    }
    await setDoc(sharedCartDoc, payload)

    // Send mail to recipient via API
    const link = `https://kudosales.com/cart?cartid=${cartId}`
    await $fetch('/api/send-cart-invite-mail', {
      method: 'POST',
      body: {
        recipientEmail: shareEmail.value,
        link,
        fromEmail: auth.user?.email || '',
        cartName: shareCartName.value.trim() || ''
      }
    })
    shareSuccess.value = 'Cart link sent!'
    toast.add({ title: 'Cart shared', description: 'A link was sent to ' + shareEmail.value + ', and added to your saved carts.', color: 'success' })
    
    shareEmail.value = ''
    shareCartName.value = ''
    shareCartPopoverOpen.value = false
  } catch (err) {
    shareError.value = 'Failed to share cart: ' + (err?.message || '')
  } finally {
    isSharingCart.value = false
  }
}

// --- LOAD SHARED CART IF cartid EXISTS IN QUERY ---
async function loadSharedCartIfPresent() {
  const cartid = route.query.cartid
  if (!cartid) return
  
  // Wait for catalog to be loaded
  await new Promise<void>((resolve) => {
    if (catalog.items.length > 0) {
      resolve()
      return
    }
    const unwatch = watchEffect(() => {
      if (catalog.items.length > 0) {
        unwatch()
        resolve()
      }
    })
  })
  
  try {
    const sharedCartResponse = await $fetch(`/api/shared-carts/${String(cartid)}`)
    if (!sharedCartResponse?.cart) {
      toast.add({ title: 'Cart link expired or deleted', color: 'error' })
      return
    }
    const sharedCart = sharedCartResponse.cart
    // Always show the modal to give users options
    overwriteCartCreatorEmail.value = sharedCart.creatorEmail || 'unknown'
    overwriteCartData.value = sharedCart
    overwriteCartModalOpen.value = true
  } catch (err) {
    toast.add({ title: 'Cart link expired or deleted', color: 'error' })
  }
}

function cartProductCount(cart): number {
  return Object.values(cart.items || {}).reduce((sum: number, obj: any) => sum + (obj.qty || 0), 0)
}

function loadSharedCart(sharedCart) {
  cart.clearAllSavedStyles()
  for (const k of Object.keys(cart.lineItems)) {
    cart.lineItems[k].qty = 0
  }
  // Sätt savedStylesArr och lineItems med rätt style/colour/size
  const stylesSet = new Set()
  if (sharedCart.items) {
    for (const [sku, obj] of Object.entries(sharedCart.items)) {
      const variant = catalog.items.find(v => v.SKU === sku)
      if (variant) {
        stylesSet.add(variant.Style)
        cart.lineItems[sku] = {
          sku,
          style: variant.Style ?? '',
          colour: variant.Colour ?? '',
          size: variant.Size ?? '',
          qty: obj.qty
        }
      }
    }
  }
  cart.savedStylesArr = Array.from(stylesSet)
  cart.persist()
  overwriteCartModalOpen.value = false
  toast.add({ title: 'Cart loaded', description: 'Shared cart loaded', color: 'success' })
}

function acceptOverwriteSharedCart() {
  if (overwriteCartData.value) {
    loadSharedCart(overwriteCartData.value)
    overwriteConfirmModalOpen.value = false
  }
}

function showOverwriteConfirmation() {
  overwriteCartModalOpen.value = false
  // Check if current cart has items
  const hasCurrentItems = Object.values(cart.lineItems).some(li => li.qty > 0)
  if (hasCurrentItems) {
    overwriteConfirmModalOpen.value = true
  } else {
    // Directly load if cart is empty
    acceptOverwriteSharedCart()
  }
}

async function saveSharedCartToMyCarts() {
  if (!overwriteCartData.value) return
  
  isSavingSharedCart.value = true
  try {
    const { $firestore } = useNuxtApp()
    if (!$firestore) throw new Error('Firestore not available')
    const { doc, setDoc, collection, Timestamp } = await import('firebase/firestore')
    
    // Generate new cart ID for the copy
    const newCartId = 'cart-' + Date.now() + '-' + Math.floor(Math.random() * 100000)
    const sharedCartDoc = doc(collection($firestore, 'sharedCarts'), newCartId)
    
    // Copy the shared cart data but with new ID and current user as creator
    // Keep the original creatorEmail to preserve the original creator
    const payload = {
      id: newCartId,
      name: overwriteCartData.value.name || '',
      items: overwriteCartData.value.items || {},
      creatorEmail: auth.user?.email || '',
      originalCreatorEmail: overwriteCartData.value.creatorEmail || '',
      timestamp: Timestamp.fromMillis(Date.now())
    }
    
    await setDoc(sharedCartDoc, payload)
    toast.add({ 
      title: 'Cart saved', 
      description: 'The shared cart was saved to your carts', 
      color: 'success' 
    })
    overwriteCartModalOpen.value = false
  } catch (err) {
    toast.add({ 
      title: 'Save failed', 
      description: 'Could not save cart to your carts', 
      color: 'error' 
    })
  } finally {
    isSavingSharedCart.value = false
  }
}

function closeClearCartPopover() {
  clearCartPopoverOpen.value = false
  detailsDrawerClearCartPopoverOpen.value = false
}

function closeSaveOrderPopover() {
  saveOrderPopoverOpen.value = false
}

async function confirmSaveOrder() {
  if (!cart.currentOrderEdit) return
  saveOrderPopoverOpen.value = false
  isPlacingOrder.value = true
  try {
    const orderId = cart.currentOrderEdit.id
    const userEmail = cart.currentOrderEdit.userEmail
    // Collect updated order data
    const lines = []
    const styles = cart.savedStylesArr
    for (const li of Object.values(cart.lineItems)) {
      if (li.qty > 0) {
        const variant = catalog.items.find(v => v.SKU === li.sku)
        lines.push({
          sku: li.sku,
          qty: li.qty,
          internalIdExportUse: variant?.InternalIDExportUse ?? ''
        })
      }
    }
    await ordersStore.updateOrder(orderId, { lines, styles })

    // Generate HTML summary for email
    let htmlSummary = ''
    for (const style of styles) {
      htmlSummary += generateStyleSummaryTable(style, lines)
    }
    const order = cart.currentOrderEdit
    const mailPayload = {
      orderId,
      userEmail,
      htmlSummary,
      accountName: order.accountName || '',
      shippingAddress: order.shippingAddress || '',
      requestedShipDate: order.requestedShipDate || '',
      poNumber: order.poNumber || ''
    }
    await $fetch('/api/send-update-mail', { method: 'POST', body: mailPayload })
    cart.clearAllSavedStyles()
    for (const sku in cart.lineItems) {
      cart.lineItems[sku].qty = 0
    }
    cart.clearOrderEdit()
    cart.persist()
    toast.add({ title: 'Order updated', description: `Order ${orderId} has been updated successfully`, color: 'success' })
    if(auth.isAdmin){
      navigateTo('/admin-orders')
    } 
    else {
      navigateTo('/orders')
    }
  } catch (err) {
    console.error('Failed to save order', err)
    toast.add({ title: 'Save failed', description: 'Could not update order', color: 'error' })
  } finally {
    isPlacingOrder.value = false
  }
}

const savedModels = computed<ProductModel[]>(() => {
  if (!ready.value) return []
  return cart.savedStylesArr
    .map(st => catalog.modelByStyle(st))
    .filter((m): m is ProductModel => !!m)
})

const savedCategories = computed<CategoryModel[]>(() => {
  if (!ready.value) return []
  return catalog.categories
    .map(cat => {
      const ms = cat.ProductModels.filter(m => cart.modelIsSaved(m.Style))
      return ms.length ? { ...cat, ProductModels: ms } : null
    })
    .filter(Boolean) as CategoryModel[]
})

function modelHasQty(model: ProductModel) {
  return model.variants.some(v => {
    const li = cart.lineItems[v.SKU]
    return li && li.qty > 0
  })
}

const modelsWithQtyCount = computed(() => {
  if (!ready.value) return 0
  return savedModels.value.filter(modelHasQty).length
})

const totalSkusWithQty = computed(
  () => Object.values(cart.lineItems).filter(li => li.qty > 0).length
)

const totalPcs = computed(
  () => Object.values(cart.lineItems).reduce((sum, li) => sum + li.qty, 0)
)

const orderTotalValue = computed(() => {
  if (!ready.value) return 0
  const field = wsFieldForCurrency(currency.value)
  let sum = 0
  for (const li of Object.values(cart.lineItems)) {
    if (li.qty <= 0) continue
    const model = catalog.modelByStyle(li.style)
    if (!model) continue
    const priceRaw = model[field]
    const price = Number(priceRaw)
    if (!Number.isFinite(price)) continue
    sum += price * li.qty
  }
  return sum
})

interface CategorySummary {
  slug: string
  name: string
  modelsWithQty: number
  skusWithQty: number
  totalItems: number
  value: number
}

const categorySummaries = computed<CategorySummary[]>(() => {
  if (!ready.value) return []
  const field = wsFieldForCurrency(currency.value)
  const summaries: CategorySummary[] = []
  for (const cat of savedCategories.value) {
    let modelsWithQty = 0
    let skusWithQty = 0
    let totalItems = 0
    let value = 0
    for (const m of cat.ProductModels) {
      if (modelHasQty(m)) modelsWithQty++
      const modelPrice = Number(m[field])
      for (const v of m.variants) {
        if (!v.SKU) continue
        const li = cart.lineItems[v.SKU]
        if (!li || li.qty <= 0) continue
        skusWithQty++
        totalItems += li.qty
        if (Number.isFinite(modelPrice)) {
          value += modelPrice * li.qty
        }
      }
    }
    summaries.push({
      slug: cat.Slug,
      name: cat.Name,
      modelsWithQty,
      skusWithQty,
      totalItems,
      value
    })
  }
  return summaries
})

watchEffect(() => {
  if (!ready.value) return
  for (const m of savedModels.value) cart.ensureModelLineItems(m)
})

function scrollToCategory(slug: string) {
  const el = document.getElementById(`category-${slug}`)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Generates an HTML summary table for a given style
function generateStyleSummaryTable(style, orderLines) {
  const model = catalog.modelByStyle(style)
  if (!model) return ''
  const field = wsFieldForCurrency(currency.value)
  // Filter relevant lines
  const lines = orderLines.filter(line => {
    const variant = catalog.items.find(v => v.SKU === line.sku)
    return variant && variant.Style === style
  })
  if (!lines.length) return ''
  
  // Create rows with variant data
  const rows = lines.map(line => {
    const variant = catalog.items.find(v => v.SKU === line.sku)
    const price = Number(variant?.[field] ?? model?.[field] ?? 0)
    return {
      modelName: model?.name ?? '',
      style: variant?.Style ?? '',
      colour: variant?.Colour ?? '',
      size: variant?.SizeCode ?? variant?.Size ?? '',
      sku: line.sku,
      qty: line.qty,
      totalPrice: price * line.qty
    }
  })
  
  // Sort rows
  rows.sort((a, b) => {
    if (a.modelName !== b.modelName) return String(a.modelName).localeCompare(String(b.modelName))
    if (a.style !== b.style) return String(a.style).localeCompare(String(b.style))
    if (a.colour !== b.colour) return String(a.colour).localeCompare(String(b.colour))
    return a.size.localeCompare(b.size)
  })
  
  const styleTotal = rows.reduce((sum, row) => sum + row.totalPrice, 0)
  
  let html = `<h3>${model.name} (Style ${model.Style})</h3>\n`
  html += `<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;margin-bottom:16px;width:700px;table-layout:fixed;">`
  html += `<thead>
    <tr>
      <th style="width: 18%;">Color</th>
      <th style="width: 12%;">Size</th>
      <th style="width: 40%;">SKU</th>
      <th style="width: 10%;">Qty</th>
      <th style="width: 20%;">Total</th>
    </tr>
  </thead><tbody>`
  
  for (const row of rows) {
    html += `<tr>
      <td>${row.colour}</td>
      <td>${row.size}</td>
      <td style="word-break:break-all;">${row.sku}</td>
      <td style="text-align:center;">${row.qty}</td>
      <td style="text-align:right;">${formatNumber(row.totalPrice, currency.value)}</td>
    </tr>`
  }
  
  html += `</tbody><tfoot>
    <tr>
      <td colspan="4" style="text-align:right;font-weight:bold;">Total for style:</td>
      <td style="text-align:right;font-weight:bold;">${formatNumber(styleTotal, currency.value)}</td>
    </tr>
  </tfoot></table>`
  
  return html
}

useHead(() => ({
  title: 'Kudo Sales demo – Your Cart',
  meta: [
    {
      name: 'description',
      content: 'Review your selected products and place your order in the Kudo Sales demo workbook.',
    },
  ],
}))
</script>
